import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { useSearchParams } from 'react-router';
import { Icon } from '../components/Icon.tsx';
import { Shell } from '../components/Shell.tsx';
import { Button, Dialog, EmptyState, Field, Skeleton, StatusBadge, type Tone } from '../components/ui.tsx';
import { t, tn, type MessageKey } from '../i18n/index.ts';
import { ApiError, request } from '../lib/api.ts';
import { day, moneyIn } from '../lib/format.ts';
import { useSession } from '../lib/session.tsx';
import { Table } from './Reports.tsx';

/**
 * The owner's page about ERP PRO itself: the plan and what it allows, ERP PRO's invoices to the
 * business, who they are made out to, and paying them by card or bank transfer.
 */

type LimitKind = 'users' | 'locations' | 'registers' | 'phones' | 'storage' | 'ai';
const LIMITS: LimitKind[] = ['users', 'locations', 'registers', 'phones', 'storage', 'ai'];
interface OfferedPlan {
  id: string;
  name: string;
  description: string | null;
  modules: string[];
  limits: Partial<Record<LimitKind, number | null>>;
  prices: Record<string, { month?: number; year?: number }>;
  trial_days: number;
}
interface BillInvoice {
  id: string;
  number: string | null;
  status: 'issued' | 'paid' | 'void';
  overdue: boolean;
  currency: string;
  issue_date: string | null;
  due_date: string | null;
  total: number;
  paid: number;
  left: number;
  description: string | null;
}
interface InvoiceDetail extends BillInvoice {
  subtotal: number;
  vat: number;
  vat_rate: number;
  lines: { description: string; quantity: number; unit_price: number; amount: number }[];
  payments: { amount: number; paid_on: string; method: string }[];
}
interface Details {
  legal_name: string | null;
  tax_id: string | null;
  address: string | null;
  city: string | null;
  billing_email: string | null;
}
interface Account {
  subscription: {
    plan_id: string | null;
    plan: string | null;
    status: 'trial' | 'active' | 'cancelled';
    state: 'trial' | 'active' | 'past_due' | 'read_only' | 'cancelled';
    currency: string;
    period: 'month' | 'year';
    trial_ends: string | null;
    paid_until: string | null;
    limits: Record<LimitKind, number | null>;
    addons?: { id: string; name: string; price: number | null }[];
  } | null;
  plan: { id: string; name: string; description: string | null; price: number | null } | null;
  usage: Record<LimitKind, number>;
  plans: OfferedPlan[];
  invoices: BillInvoice[];
  details: Details;
  banks: { bank: string; iban: string; swift: string | null; currency: string | null }[];
  issuer: { legal_name: string; email: string | null };
  cards: { provider: string; test: boolean; test_card: string | null } | null;
}
interface PaymentState {
  id: string;
  invoice_id: string;
  state: 'open' | 'succeeded' | 'failed' | 'expired';
  method: string | null;
  error: string | null;
}

const PENDING_KEY = 'erp.billing.payment';
const STATE_TONE: Record<string, { tone: Tone; icon: 'clock' | 'checkCircle' | 'alert' | 'lock' | 'x' }> = {
  trial: { tone: 'info', icon: 'clock' },
  active: { tone: 'ok', icon: 'checkCircle' },
  past_due: { tone: 'warn', icon: 'alert' },
  read_only: { tone: 'danger', icon: 'lock' },
  cancelled: { tone: 'neutral', icon: 'x' },
};
const invoiceBadge = (i: BillInvoice) =>
  i.status === 'paid' ? (
    <StatusBadge tone="ok" icon="checkCircle" label={t('bl.inv_paid')} />
  ) : i.status === 'void' ? (
    <StatusBadge tone="neutral" icon="x" label={t('bl.inv_void')} />
  ) : i.overdue ? (
    <StatusBadge tone="danger" icon="alert" label={t('bl.inv_overdue')} />
  ) : (
    <StatusBadge tone="warn" icon="clock" label={t('bl.inv_open')} />
  );

export function PlanBilling() {
  const { refresh: refreshMe } = useSession();
  const [params, setParams] = useSearchParams();
  const [a, setA] = useState<Account | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [choosing, setChoosing] = useState(false);
  const [paying, setPaying] = useState<InvoiceDetail | null>(null);
  const [opening, setOpening] = useState(false);
  const [notice, setNotice] = useState<{ tone: 'ok' | 'danger'; text: string } | null>(null);

  const load = useCallback(
    () =>
      request<Account>('GET', '/api/billing')
        .then(setA)
        .catch((e) => setError(e instanceof ApiError ? e.message : t('error.offline'))),
    [],
  );
  useEffect(() => {
    void load();
  }, [load]);

  // Back from the card provider's page: ask where the payment stands.
  useEffect(() => {
    if (!params.get('paid')) return;
    let id: string | null = null;
    try {
      id = sessionStorage.getItem(PENDING_KEY);
      sessionStorage.removeItem(PENDING_KEY);
    } catch {
      // Nothing kept: the page shows the invoices as the server has them.
    }
    const p = new URLSearchParams(params);
    p.delete('paid');
    setParams(p, { replace: true });
    if (!id) return;
    void request<PaymentState>('POST', `/api/billing/payments/${id}/refresh`)
      .then((s) => {
        setNotice(s.state === 'succeeded' ? { tone: 'ok', text: t('bl.paid_ok') } : s.state === 'open' ? { tone: 'ok', text: t('bl.paid_wait') } : { tone: 'danger', text: s.error ?? t('bl.paid_failed') });
        void load();
        void refreshMe();
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openInvoice = async (id: string) => {
    setOpening(true);
    try {
      setPaying(await request<InvoiceDetail>('GET', `/api/billing/invoices/${id}`));
    } finally {
      setOpening(false);
    }
  };
  const payNext = async () => {
    setOpening(true);
    setNotice(null);
    try {
      setPaying(await request<InvoiceDetail>('POST', '/api/billing/pay-next'));
      void load();
    } catch (e) {
      setNotice({ tone: 'danger', text: e instanceof ApiError ? e.message : t('error.offline') });
    } finally {
      setOpening(false);
    }
  };

  const sub = a?.subscription;
  const st = sub ? STATE_TONE[sub.state] : null;
  const open = a?.invoices.filter((i) => i.status === 'issued' && i.left > 0) ?? [];
  const nextDay = sub ? (sub.status === 'trial' ? sub.trial_ends : sub.paid_until) : null;

  return (
    <Shell>
      <div className="page bl-page">
        <div className="page-head">
          <div>
            <h1>{t('bl.title')}</h1>
            <p className="muted">{t('bl.subtitle')}</p>
          </div>
          {a && sub ? (
            <Button variant="primary" icon="card" busy={opening} onClick={() => (open[0] ? void openInvoice(open[0].id) : void payNext())}>
              {open[0] ? t('bl.pay_open') : t('bl.pay_next')}
            </Button>
          ) : null}
        </div>
        {notice ? (
          <p className={`callout ${notice.tone === 'ok' ? 'callout-ok' : 'callout-danger'}`} role="status">
            <Icon name={notice.tone === 'ok' ? 'checkCircle' : 'alert'} size={18} />
            <span>{notice.text}</span>
          </p>
        ) : null}
        {error ? (
          <EmptyState icon="alert" title={error} />
        ) : !a ? (
          <Skeleton h={420} r="var(--radius-lg)" />
        ) : !sub ? (
          <EmptyState icon="card" title={t('bl.no_plan')} body={t('bl.no_plan_body', { email: a.issuer.email ?? '' })} />
        ) : (
          <>
            <section className="bl-plan card" aria-labelledby="bl-plan-h">
              <div className="bl-plan-main">
                <p className="muted small" id="bl-plan-h">
                  {t('bl.your_plan')}
                </p>
                <h2>{a.plan?.name ?? sub.plan ?? '–'}</h2>
                {st ? <StatusBadge tone={st.tone} icon={st.icon} label={t(`bl.state_${sub.state}` as MessageKey)} /> : null}
                {a.plan?.description ? <p className="muted">{a.plan.description}</p> : null}
              </div>
              <dl className="fiscal-stats bl-stats">
                <div className="fiscal-stat">
                  <dt>{t('bl.price')}</dt>
                  <dd>{a.plan?.price != null ? `${moneyIn(a.plan.price, sub.currency)} / ${t(sub.period === 'year' ? 'bl.year' : 'bl.month')}` : '–'}</dd>
                </div>
                {(sub.addons ?? []).map((x) => (
                  <div key={x.id} className="fiscal-stat">
                    <dt>{t('bl.addon', { name: x.name })}</dt>
                    <dd>{x.price != null ? `${moneyIn(x.price, sub.currency)} / ${t(sub.period === 'year' ? 'bl.year' : 'bl.month')}` : '–'}</dd>
                  </div>
                ))}
                <div className={`fiscal-stat${sub.state === 'past_due' || sub.state === 'read_only' ? ' danger' : ''}`}>
                  <dt>{t(sub.status === 'trial' ? 'bl.trial_until' : 'bl.paid_until')}</dt>
                  <dd>{nextDay ? day(nextDay) : '–'}</dd>
                </div>
                <div className={`fiscal-stat${open.length ? ' warn' : ''}`}>
                  <dt>{t('bl.to_pay')}</dt>
                  <dd>{open.length ? moneyIn(open.reduce((s, i) => s + i.left, 0), open[0]!.currency) : moneyIn(0, sub.currency)}</dd>
                </div>
              </dl>
              {a.plans.length ? (
                <Button variant="secondary" icon="repeat" onClick={() => setChoosing(true)}>
                  {t('bl.change_plan')}
                </Button>
              ) : null}
            </section>

            <section className="card bl-usage" aria-labelledby="bl-usage-h">
              <h2 id="bl-usage-h">{t('bl.usage')}</h2>
              <ul>
                {LIMITS.map((k) => {
                  const max = sub.limits[k];
                  const used = a.usage[k] ?? 0;
                  const pct = max ? Math.min(100, Math.round((used / max) * 100)) : 0;
                  return (
                    <li key={k}>
                      <span className="bl-usage-label">{t(`platform.limit_${k}` as MessageKey)}</span>
                      <span className="bl-usage-n">{max == null ? t('bl.used_no_limit', { n: used }) : t('bl.used_of', { n: used, max })}</span>
                      {max == null ? null : (
                        <span className={`bl-meter${pct >= 100 ? ' full' : pct >= 80 ? ' near' : ''}`} role="meter" aria-valuemin={0} aria-valuemax={max} aria-valuenow={used} aria-label={t(`platform.limit_${k}` as MessageKey)}>
                          <span style={{ width: `${pct}%` }} />
                        </span>
                      )}
                    </li>
                  );
                })}
              </ul>
            </section>

            <section className="bl-invoices" aria-labelledby="bl-inv-h">
              <h2 id="bl-inv-h">{t('bl.invoices')}</h2>
              {a.invoices.length ? (
                <Table
                  head={[t('bl.number'), t('bl.issued'), t('bl.due'), t('bl.total'), t('bl.status')]}
                  left={[0]}
                  phoneHide={[1, 2]}
                  rows={a.invoices.map((i) => ({
                    key: i.id,
                    onClick: () => void openInvoice(i.id),
                    cells: [i.number ?? '–', i.issue_date ? day(i.issue_date) : '–', i.due_date ? day(i.due_date) : '–', moneyIn(i.total, i.currency), invoiceBadge(i)],
                  }))}
                />
              ) : (
                <EmptyState icon="invoice" title={t('bl.no_invoices')} body={sub.status === 'trial' ? t('bl.no_invoices_trial') : undefined} />
              )}
            </section>

            <DetailsForm details={a.details} onSaved={(n) => (setA(n), setNotice({ tone: 'ok', text: t('bl.details_saved') }))} />
          </>
        )}
      </div>
      {choosing && a && sub ? (
        <ChoosePlan
          account={a}
          onClose={() => setChoosing(false)}
          onDone={(n) => {
            setA(n);
            setChoosing(false);
            setNotice({ tone: 'ok', text: t('bl.plan_changed') });
            void refreshMe();
          }}
        />
      ) : null}
      {paying && a ? (
        <PayInvoice
          invoice={paying}
          account={a}
          onClose={() => setPaying(null)}
          onPaid={() => {
            setPaying(null);
            setNotice({ tone: 'ok', text: t('bl.paid_ok') });
            void load();
            void refreshMe();
          }}
        />
      ) : null}
    </Shell>
  );
}

function DetailsForm({ details, onSaved }: { details: Details; onSaved: (a: Account) => void }) {
  const [d, setD] = useState({
    legal_name: details.legal_name ?? '',
    tax_id: details.tax_id ?? '',
    address: details.address ?? '',
    city: details.city ?? '',
    billing_email: details.billing_email ?? '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const set = (p: Partial<typeof d>) => (setD((x) => ({ ...x, ...p })), setError(null));
  const save = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      onSaved(await request<Account>('PUT', '/api/billing/details', { ...d, city: d.city || null }));
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.offline'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form className="card bl-details" onSubmit={save} aria-labelledby="bl-det-h" noValidate>
      <h2 id="bl-det-h">{t('bl.details')}</h2>
      <p className="muted">{t('bl.details_intro')}</p>
      <div className="form-grid">
        <Field label={t('bl.legal_name')} required>
          <input className="input" value={d.legal_name} maxLength={160} onChange={(e) => set({ legal_name: e.target.value })} />
        </Field>
        <Field label={t('bl.tax_id')} required>
          <input className="input" value={d.tax_id} maxLength={40} onChange={(e) => set({ tax_id: e.target.value })} />
        </Field>
        <Field label={t('bl.address')} required>
          <input className="input" value={d.address} maxLength={200} autoComplete="street-address" onChange={(e) => set({ address: e.target.value })} />
        </Field>
        <Field label={t('bl.city')}>
          <input className="input" value={d.city} maxLength={80} autoComplete="address-level2" onChange={(e) => set({ city: e.target.value })} />
        </Field>
        <Field label={t('bl.billing_email')} hint={t('bl.billing_email_hint')} required>
          <input className="input" type="email" value={d.billing_email} maxLength={200} onChange={(e) => set({ billing_email: e.target.value })} />
        </Field>
      </div>
      {error ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      ) : null}
      <div className="form-actions">
        <Button variant="primary" type="submit" busy={busy}>
          {t('common.save')}
        </Button>
      </div>
    </form>
  );
}

function ChoosePlan({ account, onClose, onDone }: { account: Account; onClose: () => void; onDone: (a: Account) => void }) {
  const sub = account.subscription!;
  const [plan, setPlan] = useState(sub.plan_id ?? '');
  const [period, setPeriod] = useState<'month' | 'year'>(sub.period);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const priceOf = (p: OfferedPlan, per: 'month' | 'year') => p.prices[sub.currency]?.[per] ?? null;
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      onDone(await request<Account>('POST', '/api/billing/plan', { plan, period }));
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.offline'));
      setBusy(false);
    }
  };
  const unchanged = plan === sub.plan_id && period === sub.period;
  return (
    <Dialog
      title={t('bl.change_plan')}
      size="lg"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button variant="primary" busy={busy} disabled={unchanged || !plan} onClick={() => void save()}>
            {t('bl.change_confirm')}
          </Button>
        </>
      }
    >
      <div className="bl-period" role="radiogroup" aria-label={t('bl.period')}>
        {(['month', 'year'] as const).map((p) => (
          <label key={p} className={`bl-period-opt${period === p ? ' on' : ''}`}>
            <input type="radio" name="period" checked={period === p} onChange={() => setPeriod(p)} />
            {t(p === 'month' ? 'bl.monthly' : 'bl.yearly')}
          </label>
        ))}
      </div>
      <div className="signup-plans" role="radiogroup" aria-label={t('bl.your_plan')}>
        {account.plans.map((p) => {
          const price = priceOf(p, period);
          return (
            <label key={p.id} className={`signup-plan${plan === p.id ? ' on' : ''}${price == null ? ' off' : ''}`}>
              <input type="radio" name="plan" value={p.id} checked={plan === p.id} disabled={price == null} onChange={() => setPlan(p.id)} />
              <span className="signup-plan-head">
                <strong>{p.name}</strong>
                {p.id === sub.plan_id ? <span className="signup-plan-trial">{t('bl.current')}</span> : null}
              </span>
              <span className="signup-plan-price">
                {price == null ? (
                  <span className="muted">{t('bl.not_offered', { currency: sub.currency })}</span>
                ) : (
                  <>
                    <b>{moneyIn(price, sub.currency)}</b> {t(period === 'year' ? 'signup.per_year' : 'signup.per_month')}
                  </>
                )}
              </span>
              {p.description ? <span className="signup-plan-desc">{p.description}</span> : null}
              <span className="muted small">{p.modules.length ? tn('signup.apps', p.modules.length) : t('signup.apps_core')}</span>
            </label>
          );
        })}
      </div>
      <p className="muted small">{t(sub.status === 'trial' ? 'bl.change_note_trial' : 'bl.change_note')}</p>
      {error ? (
        <p className="form-error" role="alert">
          <Icon name="alert" size={16} /> {error}
        </p>
      ) : null}
    </Dialog>
  );
}

function PayInvoice({ invoice, account, onClose, onPaid }: { invoice: InvoiceDetail; account: Account; onClose: () => void; onPaid: () => void }) {
  const [card, setCard] = useState('');
  const [payment, setPayment] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const due = invoice.status === 'issued' && invoice.left > 0;
  const banks = account.banks.filter((b) => !b.currency || b.currency === invoice.currency);

  const startCard = async () => {
    setBusy(true);
    setError(null);
    try {
      const c = await request<{ payment: string; url: string | null; simulator: boolean }>('POST', `/api/billing/invoices/${invoice.id}/checkout`, {
        return_url: `${window.location.origin}/billing?paid=1`,
      });
      if (c.url) {
        try {
          sessionStorage.setItem(PENDING_KEY, c.payment);
        } catch {
          // Back on the page, the invoices show what the provider confirmed.
        }
        window.location.assign(c.url);
        return;
      }
      setPayment(c.payment);
      setBusy(false);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t('error.offline'));
      setBusy(false);
    }
  };
  const payTest = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const s = await request<PaymentState>('POST', `/api/billing/payments/${payment}/card`, { card });
      if (s.state === 'succeeded') onPaid();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('error.offline'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog title={t('bl.invoice', { number: invoice.number ?? '' })} size="md" onClose={onClose}>
      <div className="bl-pay-head">
        {invoiceBadge(invoice)}
        <span className="muted small">
          {invoice.issue_date ? t('bl.issued_on', { date: day(invoice.issue_date) }) : null}
          {invoice.due_date ? ` · ${t('bl.due_on', { date: day(invoice.due_date) })}` : null}
        </span>
      </div>
      <ul className="bl-lines">
        {invoice.lines.map((l, i) => (
          <li key={i}>
            <span>{l.description}</span>
            <b>{moneyIn(l.amount, invoice.currency)}</b>
          </li>
        ))}
        {invoice.vat ? (
          <li>
            <span>{t('bl.vat', { rate: invoice.vat_rate })}</span>
            <b>{moneyIn(invoice.vat, invoice.currency)}</b>
          </li>
        ) : null}
        <li className="bl-lines-total">
          <span>{t('bl.total')}</span>
          <b>{moneyIn(invoice.total, invoice.currency)}</b>
        </li>
        {invoice.paid > 0 && due ? (
          <li className="bl-lines-total">
            <span>{t('bl.left')}</span>
            <b>{moneyIn(invoice.left, invoice.currency)}</b>
          </li>
        ) : null}
      </ul>
      {!due ? null : payment ? (
        <form className="bl-card-form" onSubmit={payTest} noValidate>
          <p className="callout callout-info">
            <Icon name="info" size={18} />
            <span>{t('bl.test_mode', { card: account.cards?.test_card?.replace(/(\d{4})(?=\d)/g, '$1 ') ?? '' })}</span>
          </p>
          <Field label={t('bl.card_number')}>
            <input className="input" inputMode="numeric" autoComplete="cc-number" maxLength={23} value={card} onChange={(e) => (setCard(e.target.value.replace(/[^\d ]/g, '')), setError(null))} data-autofocus />
          </Field>
          {error ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={16} /> {error}
            </p>
          ) : null}
          <Button variant="primary" size="lg" type="submit" busy={busy} className="w-full" icon="lock">
            {t('bl.pay_amount', { amount: moneyIn(invoice.left, invoice.currency) })}
          </Button>
        </form>
      ) : (
        <>
          {account.cards ? (
            <Button variant="primary" size="lg" icon="card" className="w-full" busy={busy} onClick={() => void startCard()}>
              {t('bl.pay_card', { amount: moneyIn(invoice.left, invoice.currency) })}
            </Button>
          ) : null}
          {error ? (
            <p className="form-error" role="alert">
              <Icon name="alert" size={16} /> {error}
            </p>
          ) : null}
          <div className="bl-bank">
            <h3>{t('bl.by_transfer')}</h3>
            {banks.length ? (
              <ul>
                {banks.map((b) => (
                  <li key={b.iban}>
                    <strong>{b.bank}</strong>
                    <span className="mono">{b.iban.replace(/(.{4})(?=.)/g, '$1 ')}</span>
                    {b.swift ? <span className="muted small">SWIFT/BIC {b.swift}</span> : null}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="muted">{t('bl.no_banks', { email: account.issuer.email ?? '' })}</p>
            )}
            <p className="muted small">{t('bl.transfer_ref', { number: invoice.number ?? '' })}</p>
          </div>
        </>
      )}
    </Dialog>
  );
}
